import React, { createContext, useState, useCallback, useEffect } from 'react'

export interface InAppNotification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  message: string
  timestamp: number
}

interface NotificationContextType {
  notifications: InAppNotification[]
  addNotification: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void
  removeNotification: (id: string) => void
  requestNotificationPermission: () => Promise<NotificationPermission>
  notificationPermission: NotificationPermission | null
  soundEnabled: boolean
  setSoundEnabled: (enabled: boolean) => void
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([])
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | null>(null)
  const [soundEnabled, setSoundEnabled] = useState(true)

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission)
    }
  }, [])

  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      return 'denied'
    }

    if (Notification.permission !== 'default') {
      return Notification.permission
    }

    const permission = await Notification.requestPermission()
    setNotificationPermission(permission)
    return permission
  }, [])

  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 800
      oscillator.type = 'sine'

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
    } catch {
      // Audio context not available
    }
  }, [])

  const addNotification = useCallback((message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const id = Math.random().toString(36).substr(2, 9)
    const notification: InAppNotification = {
      id,
      type,
      message,
      timestamp: Date.now()
    }

    setNotifications((prev) => [...prev, notification])

    // Auto-remove after 5 seconds — use setNotifications to avoid stale closure
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, 5000)

    // Play sound if enabled
    if (soundEnabled && type === 'info') {
      playNotificationSound()
    }
  }, [soundEnabled, playNotificationSound])

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    requestNotificationPermission,
    notificationPermission,
    soundEnabled,
    setSoundEnabled
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = React.useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
